import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCancelScheduledPhonePlans, useReactivateMobileLine, useSuspendMobileLine, useTerminateMobileLine, useUnsuspendMobileLine, useUpgradeMobileLinePhonePlan } from '@/hooks/useLineActions';
import { useLine } from '@/hooks/useLines';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { fadeUp, stagger } from '@/lib/animations';
import { motion } from 'framer-motion';
import {
  BarChart3,
  ChevronLeft,
  CreditCard,
  Globe,
  Info,
  ReceiptEuro,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ChangePlanDialog } from './line-details/ChangePlanDialog';
import { ConfirmActionDialog } from './line-details/ConfirmActionDialog';
import { LineBillingTab } from './line-details/LineBillingTab';
import { LineConsumptionTab } from './line-details/LineConsumptionTab';
import { LineDetailsSkeleton } from './line-details/LineDetailsSkeleton';
import { LineHeroCard } from './line-details/LineHeroCard';
import { LineInfoTab } from './line-details/LineInfoTab';
import { LineSimTab } from './line-details/LineSimTab';
import { LineTravelEsimsTab } from './line-details/LineTravelEsimsTab';
import { LineStatusCallouts } from './line-details/LineStatusCallouts';
import { UpdateUserDialog } from './line-details/UpdateUserDialog';

/** Tab ids, in the order they appear. Also the accepted `?tab=` values. */
const TABS = ['info', 'consumption', 'sim', 'travel-esim', 'billing'] as const;

/** The mutually-exclusive lifecycle confirmations of the view. */
type LifecycleAction =
  | 'suspend'
  | 'unsuspend'
  | 'terminate'
  | 'cancelTermination'
  | 'cancelPlanChange';

export function LineDetailsView() {
  const { lineId } = useParams();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  // The open tab lives in the URL so a link can land straight on it (the
  // consumption alerts point here) and so a reload keeps the tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab === tabParam)
    ? (tabParam as (typeof TABS)[number])
    : 'info';
  const selectTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    // Switching tabs is not a navigation step: it must not stack history
    // entries between the previous page and this one.
    setSearchParams(next, { replace: true });
  };

  const [lifecycleAction, setLifecycleAction] =
    useState<LifecycleAction | null>(null);
  const [updateUserDialog, setUpdateUserDialog] = useState(false);
  const [changePlanDialog, setChangePlanDialog] = useState(false);

  const {
    data: lineDetails,
    isLoading: isLoadingLine,
    error: lineError,
  } = useLine(lineId);

  const suspend = useSuspendMobileLine();
  const unsuspend = useUnsuspendMobileLine();
  const terminate = useTerminateMobileLine();
  const reactivate = useReactivateMobileLine();
  const cancelPlanChange = useCancelScheduledPhonePlans();
  const { mutate: upgradeMobileLinePhonePlan, isPending: isUpgrading } =
    useUpgradeMobileLinePhonePlan();

  if (isLoadingLine) return <LineDetailsSkeleton />;
  if (lineError) return <div>Error loading data</div>;
  if (!lineDetails) return <div>Ligne non trouvée</div>;

  const handleBack = () => navigate(`/o/${tenantSlug}/lines`);

  const handleToggleSuspend = () => {
    setLifecycleAction(
      lineDetails.status === 'suspended' ? 'unsuspend' : 'suspend',
    );
  };

  const isActionPending =
    suspend.isPending || unsuspend.isPending || terminate.isPending;

  // One entry per lifecycle confirmation: the dialog below renders whichever
  // action is requested, with identical close-on-success/toast semantics.
  const lifecycle: Record<
    LifecycleAction,
    {
      title: string;
      description: string;
      actionLabel: string;
      pendingLabel: string;
      variant: 'default' | 'destructive';
      mutation: typeof suspend;
      successMessage: string;
      errorPrefix: string;
    }
  > = {
    suspend: {
      title: 'Suspendre la ligne',
      description: `Êtes-vous sûr de vouloir suspendre la ligne ${lineDetails.number} ? Cette action suspendra temporairement tous les services.`,
      actionLabel: 'Suspendre',
      pendingLabel: 'Suspension...',
      variant: 'default',
      mutation: suspend,
      successMessage: 'Ligne suspendue avec succès.',
      errorPrefix: 'Erreur lors de la suspension',
    },
    unsuspend: {
      title: 'Réactiver la ligne',
      description: `Êtes-vous sûr de vouloir réactiver la ligne ${lineDetails.number} ? Les services seront rétablis.`,
      actionLabel: 'Réactiver',
      pendingLabel: 'Réactivation...',
      variant: 'default',
      mutation: unsuspend,
      successMessage: 'Ligne réactivée avec succès.',
      errorPrefix: 'Erreur lors de la réactivation',
    },
    terminate: {
      title: 'Résilier la ligne',
      description: `Êtes-vous sûr de vouloir résilier la ligne ${lineDetails.number} ? La résiliation sera effective en fin de mois, de manière irréversible. Vous pourrez néanmoins l'annuler jusqu'au dernier jour du mois en cours.`,
      actionLabel: 'Lancer la résiliation',
      pendingLabel: 'Résiliation...',
      variant: 'destructive',
      mutation: terminate,
      successMessage: 'La demande de résiliation a été prise en compte.',
      errorPrefix: 'Erreur lors de la résiliation',
    },
    cancelTermination: {
      title: 'Annuler la résiliation',
      description: `Êtes-vous sûr de vouloir annuler la résiliation de la ligne ${lineDetails.number} ? La ligne redeviendra active.`,
      actionLabel: 'Annuler la résiliation',
      pendingLabel: 'Annulation...',
      variant: 'default',
      mutation: reactivate,
      successMessage: 'La résiliation a été annulée avec succès.',
      errorPrefix: "Erreur lors de l'annulation de la résiliation",
    },
    cancelPlanChange: {
      title: 'Annuler le changement de forfait',
      description:
        'Êtes-vous sûr de vouloir annuler le changement de forfait programmé ?',
      actionLabel: 'Annuler le changement',
      pendingLabel: 'Annuler le changement',
      variant: 'default',
      mutation: cancelPlanChange,
      successMessage: 'Le changement de forfait a été annulé avec succès.',
      errorPrefix: "Erreur lors de l'annulation du changement de forfait",
    },
  };

  const currentLifecycle = lifecycleAction ? lifecycle[lifecycleAction] : null;

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* ── Back button ────────────────────────────────── */}
      <motion.div variants={fadeUp}>
        <button
          onClick={handleBack}
          className="group flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-400 transition-all hover:bg-white hover:text-slate-600 hover:shadow-sm"
        >
          <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Retour aux lignes
        </button>
      </motion.div>

      <LineStatusCallouts
        lineDetails={lineDetails}
        isReactivating={reactivate.isPending}
        onCancelTermination={() => setLifecycleAction('cancelTermination')}
        onCancelPlanChange={() => setLifecycleAction('cancelPlanChange')}
      />

      <LineHeroCard
        lineDetails={lineDetails}
        isActionPending={isActionPending}
        onUpdateUser={() => setUpdateUserDialog(true)}
        onChangePlan={() => setChangePlanDialog(true)}
        onToggleSuspend={handleToggleSuspend}
        onTerminate={() => setLifecycleAction('terminate')}
        onOrderTravelEsim={() =>
          navigate(
            `/o/${tenantSlug}/catalog?tab=travel-esim&line=${lineDetails.id}`,
          )
        }
      />

      {/* ── Tabs ───────────────────────────────────────── */}
      <motion.div variants={fadeUp}>
        <Tabs value={activeTab} onValueChange={selectTab} className="space-y-4">
          <TabsList className="bg-white shadow-sm rounded-xl h-12 p-1 w-full sm:w-auto">
            <TabsTrigger
              value="info"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <Info className="h-4 w-4" />
              Informations
            </TabsTrigger>
            <TabsTrigger
              value="consumption"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              Consommation
            </TabsTrigger>
            <TabsTrigger
              value="sim"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <CreditCard className="h-4 w-4" />
              Carte SIM
            </TabsTrigger>
            <TabsTrigger
              value="travel-esim"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <Globe className="h-4 w-4" />
              E-SIM Internationale
            </TabsTrigger>
            <TabsTrigger
              value="billing"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <ReceiptEuro className="h-4 w-4" />
              Facturation
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info">
            <LineInfoTab lineId={lineId!} lineDetails={lineDetails} />
          </TabsContent>

          <TabsContent value="consumption">
            <LineConsumptionTab
              lineId={lineId}
              lineDetails={lineDetails}
              tenantSlug={tenantSlug}
            />
          </TabsContent>

          <TabsContent value="sim">
            <LineSimTab lineId={lineId!} lineDetails={lineDetails} />
          </TabsContent>

          <TabsContent value="travel-esim">
            <LineTravelEsimsTab lineId={lineId!} tenantSlug={tenantSlug} />
          </TabsContent>

          <TabsContent value="billing">
            <LineBillingTab lineId={lineId} />
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* ── Dialogs ────────────────────────────────────── */}
      {currentLifecycle && (
        <ConfirmActionDialog
          isOpen
          onClose={() => setLifecycleAction(null)}
          onConfirm={() => {
            currentLifecycle.mutation.mutate(
              { lineId: lineId! },
              {
                onSuccess: () => {
                  setLifecycleAction(null);
                  toast.success(currentLifecycle.successMessage);
                },
                onError: (error) => {
                  toast.error(
                    `${currentLifecycle.errorPrefix}: ${
                      error.message || 'Erreur inconnue'
                    }`,
                  );
                },
              },
            );
          }}
          title={currentLifecycle.title}
          description={currentLifecycle.description}
          actionLabel={
            currentLifecycle.mutation.isPending
              ? currentLifecycle.pendingLabel
              : currentLifecycle.actionLabel
          }
          variant={currentLifecycle.variant}
          isLoading={currentLifecycle.mutation.isPending}
        />
      )}

      {updateUserDialog && (
        <UpdateUserDialog
          isOpen={updateUserDialog}
          onClose={() => setUpdateUserDialog(false)}
          defaultValues={{
            firstName: lineDetails.identity?.firstName || '',
            lastName: lineDetails.identity?.lastName || '',
          }}
          identityId={lineDetails.identity?.id || ''}
        />
      )}

      {changePlanDialog && lineDetails.phonePlan && (
        <ChangePlanDialog
          isOpen={changePlanDialog}
          onClose={() => setChangePlanDialog(false)}
          currentPlan={lineDetails.phonePlan}
          organizationId={lineDetails.site.organizationId}
          isUpgrading={isUpgrading}
          lineDetails={lineDetails}
          onConfirm={(planId) => {
            upgradeMobileLinePhonePlan(
              {
                lineId: lineId!,
                phonePlanId: planId,
              },
              {
                onSuccess: () => {
                  toast.success(
                    'Le changement de forfait a été réalisé avec succès.',
                  );
                  setChangePlanDialog(false);
                },
                onError: (error) => {
                  toast.error(
                    `Erreur lors de la modification du plan: ${
                      error.message || 'Erreur inconnue'
                    }`,
                  );
                },
              },
            );
          }}
        />
      )}
    </motion.div>
  );
}
